import { createTheme, CssBaseline, ThemeProvider, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AuthProvider, useAuthenticated } from "nate-react-api-helpers";
import { PropsWithChildren, ReactNode } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { api } from "./api/API";
import { ErrorBoundary } from "./ErrorBoundary";
import { useCurrentUser } from "./misc/CurrentUser";
import { FeedProvider } from "./misc/Feed";
import { Layout } from "./misc/Layout";
import { LoginRedirector } from "./misc/LoginRedirector";
import { Redirect } from "./misc/Redirect";
import { Snackbar } from "./misc/Snackbar";
import { UIFlagProvider } from "./misc/UIFlags";
import {
    allWorkspace, lastWorkspace, RememberWorkspace, switchPath, useIsPlainAdmin, useWorkspace, WorkspaceProvider
} from "./misc/Workspace";
import { AdminPage } from "./pages/admin/AdminPage";
import { ApiTokenListPage } from "./pages/apitoken/ApiTokenListPage";
import { ConfirmPage } from "./pages/account/ConfirmPage";
import { ForgotPasswordPage } from "./pages/account/ForgotPasswordPage";
import { InvitePage } from "./pages/account/InvitePage";
import { RegisterPage } from "./pages/account/RegisterPage";
import { ResetPasswordPage } from "./pages/account/ResetPasswordPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ProjectGanttPage } from "./pages/project/ProjectGanttPage";
import { ProjectListPage } from "./pages/project/ProjectListPage";
import { LoginPage } from "./pages/login/LoginPage";
import { TaskCalendarPage } from "./pages/task/calendar/TaskCalendarPage";
import { TaskDayPage } from "./pages/task/day/TaskDayPage";
import { TaskSearchPage } from "./pages/task/search/TaskSearchPage";
import { TasksHome } from "./pages/task/TasksHome";
import { TeamListPage } from "./pages/team/TeamListPage";
import { TeamSettingsPage } from "./pages/team/TeamSettingsPage";

// the pages reached without logging in
const authIgnore = ["/login", "/register", "/confirm", "/forgot-password", "/reset-password", "/invite"];

// the app works on a grey canvas: a page puts its content on white surfaces so the
// content reads as content rather than as the window it sits in
const theme = createTheme({
    palette: {
        background: { default: grey[100] },
    },
});

// while logged out, keep the protected routes unmounted so their data loads don't
// fire against a session that isn't there yet.
function AuthGate(props: { children: ReactNode }) {
    const auth = useAuthenticated();
    const loc = useLocation();

    if (!auth.authenticated && !authIgnore.includes(loc.pathname)) return null;
    return <>{props.children}</>;
}

// Home opens the last workspace, or the admin page for the admin account
function Home() {
    const me = useCurrentUser();
    const plainAdmin = useIsPlainAdmin();

    // the ping says who's logged in; until then there's nowhere to send them
    if (!me) return null;

    return <Redirect to={plainAdmin ? "/admin" : "/w/" + lastWorkspace() + "/tasks"} />;
}

// OldPath sends a link from before workspaces to the same page in the last workspace
function OldPath() {
    const loc = useLocation();
    const page = loc.pathname === "/tasks/list" ? "/search" : loc.pathname;

    return <Redirect to={"/w/" + lastWorkspace() + page + loc.search} />;
}

// WorkspacePage is a page of one workspace. The admin account has none, so it's sent
// to the admin page.
function WorkspacePage(props: PropsWithChildren<{ fullBleed?: boolean }>) {
    const me = useCurrentUser();
    const plainAdmin = useIsPlainAdmin();
    const ws = useWorkspace();
    const loc = useLocation();

    if (!me) return null;
    if (plainAdmin) return <Redirect to="/admin" />;

    // a team the user isn't on any more (left, removed, archived) opens "All" instead
    const teamGone = !!ws.team && !ws.loading && !ws.error && !ws.current;
    if (teamGone) return <Redirect to={switchPath(loc, allWorkspace)} />;

    return (
        <Layout fullBleed={props.fullBleed}>
            <RememberWorkspace />
            {props.children}
        </Layout>
    );
}

function AdminOnly(props: PropsWithChildren<{}>) {
    const me = useCurrentUser();
    const plainAdmin = useIsPlainAdmin();

    if (!me) return null;
    if (!plainAdmin) return <Redirect to="/" />;

    return <Layout>{props.children}</Layout>;
}

function App() {
    return (
        <ErrorBoundary fallback={(err, reset) => (
            <div style={{ padding: 20 }}>
                <Typography variant="h6">Oops, there was an error!</Typography>
                <pre>{err.toString()}</pre>
                <button onClick={reset}>Reset</button>
            </div>
        )}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterMoment}>
                <AuthProvider onPing={() => api.users.ping()}>
                    <Snackbar timeout={10 * 1000}>
                        <LoginRedirector ignore={authIgnore} />
                        <AuthGate>
                            <WorkspaceProvider>
                            <FeedProvider>
                            <UIFlagProvider>
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/register" element={<RegisterPage />} />
                                <Route path="/confirm" element={<ConfirmPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                                <Route path="/invite" element={<InvitePage />} />

                                <Route path="/" element={<Home />} />
                                <Route path="/tasks" element={<OldPath />} />
                                <Route path="/tasks/calendar" element={<OldPath />} />
                                <Route path="/tasks/day" element={<OldPath />} />
                                <Route path="/tasks/list" element={<OldPath />} />
                                <Route path="/projects" element={<OldPath />} />
                                <Route path="/projects/:id/gantt" element={<OldPath />} />

                                <Route path="/w/:workspace" element={<WorkspacePage><TasksHome /></WorkspacePage>} />
                                <Route path="/w/:workspace/tasks" element={<WorkspacePage><TasksHome /></WorkspacePage>} />
                                <Route path="/w/:workspace/tasks/calendar" element={<WorkspacePage fullBleed><TaskCalendarPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/tasks/calendar/new" element={<WorkspacePage fullBleed><TaskCalendarPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/tasks/day" element={<WorkspacePage fullBleed><TaskDayPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/tasks/day/new" element={<WorkspacePage fullBleed><TaskDayPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/search" element={<WorkspacePage><TaskSearchPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/projects" element={<WorkspacePage><ProjectListPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/projects/:id/gantt" element={<WorkspacePage><ProjectGanttPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/team" element={<WorkspacePage><TeamSettingsPage /></WorkspacePage>} />
                                <Route path="/w/:workspace/teams" element={<WorkspacePage><TeamListPage /></WorkspacePage>} />

                                <Route path="/api-tokens" element={<Layout><ApiTokenListPage /></Layout>} />
                                <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
                                <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
                            </Routes>
                            </UIFlagProvider>
                            </FeedProvider>
                            </WorkspaceProvider>
                        </AuthGate>
                    </Snackbar>
                </AuthProvider>
                </LocalizationProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
